import { ReactNode } from 'react';
import { Title, Chat } from '@mui/icons-material';

export interface MenuItem {
  icon: () => ReactNode;
  label: string;
  onClick: () => void;
  subMenuItems?: MenuItem[];
}

export type Menu = Record<'new' | 'edit' | 'confirm', MenuItem[]>;

interface Prompt {
  label: string;
  body: string;
  field?: string;
  retry?: boolean;
}

/**
 * Processes prompt body by replacing ${currentField} with actual field value
 */
const processPromptBody = (promptBody: string, fieldValue: string): string => {
  return promptBody.includes('${currentField}')
    ? promptBody.replace(/\${currentField}/g, fieldValue)
    : `${promptBody} ${fieldValue}`;
};

/**
 * Creates generate options from prompts
 */
export const createGenerateOptions = (
  prompts: Prompt[],
  onGenerate: (content: string) => void,
): MenuItem[] => {
  return prompts.map((prompt) => ({
    icon: () => <Title />,
    label: prompt.label,
    onClick: () => onGenerate(`${prompt.body}`),
  }));
};

/**
 * Creates retry options from prompts
 */
/**
 * Parse JSON response and extract titles array
 * Handles both valid JSON and malformed JSON with unquoted property names and strings
 */
export const parseTitles = (jsonString: string): string[] => {
  try {
    // First try to parse as-is
    const parsed = JSON.parse(jsonString);
    return Array.isArray(parsed.titles) ? parsed.titles : [];
  } catch (error) {
    try {
      // Fix malformed JSON by adding quotes around property names and string values
      const fixedJson = jsonString
        .replace(/(\w+):/g, '"$1":') // Add quotes around property names
        .replace(/\[([\s\S]*?)\]/g, (match, content) => {
          // Fix array content by splitting on newlines and adding quotes around unquoted strings
          const items = content
            .split('\n')
            .map((item: string) => {
              const trimmed = item.trim();
              // Remove leading and trailing commas
              const cleanItem = trimmed
                .replace(/^,\s*/, '')
                .replace(/,\s*$/, '');
              if (
                cleanItem &&
                !cleanItem.startsWith('"') &&
                !cleanItem.endsWith('"')
              ) {
                return `"${cleanItem}"`;
              }
              return cleanItem;
            })
            .filter((item: string) => item); // Remove empty items
          return `[${items.join(',')}]`;
        });

      const parsed = JSON.parse(fixedJson);
      return Array.isArray(parsed.titles) ? parsed.titles : [];
    } catch (fixError) {
      return [];
    }
  }
};

export const createRetryOptions = (
  prompts: Prompt[],
  fieldValue: string,
  handleOpenAi: (
    content: string,
    maxTokens?: number,
    temperature?: number,
  ) => Promise<any>,
  onRetry: (suggestions: string[]) => void,
  setIsLoading: (loading: boolean) => void,
  maxTokens?: number,
  temperature?: number,
): MenuItem[] => {
  return prompts.map((prompt) => ({
    icon: () => <Chat />,
    label: prompt.label,
    onClick: async () => {
      setIsLoading(true);
      try {
        const value = fieldValue || '';
        const promptBody = processPromptBody(prompt.body, value);
        const result = await handleOpenAi(promptBody, maxTokens, temperature);
        const responseContent = result.data?.executeTextPrompt?.content || '';

        const suggestions = parseTitles(responseContent);
        onRetry(suggestions);
      } finally {
        setIsLoading(false);
      }
    },
  }));
};
